import React, { useState, useMemo, JSX } from "react";
import "./UserDetails.css";
import { PlusCircleIcon } from "../../../../icons/reactIcons";
import StepIcon from "../../../../icons/svg-cons/StepIcon";
import Active24Icon from "../../../../icons/svg-cons/Active24Icon";
import NutritionIcon from "../../../../icons/svg-cons/NutritionIcon";
import SleepBedIcon from "../../../../icons/svg-cons/SleepBedIcon";
import MindFullIcon from "../../../../icons/svg-cons/MindFullIcon";
import ActivityModal from "../user-modal/ActivityModal";
import { ActivityData } from "../../../../apis/userApis";
import {
  addUserActivity,
  updateUserActivity,
} from "../../../../apis/userActivityApis";
import WaterGlassIcon from "../../../../icons/svg-cons/WaterGlassIcon";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../redux/store/store";
import { fetchUserDetails } from "../../../../redux/slices/userListSlice";

/* ================= Props ================= */

interface UserActivityProps {
  activityData: ActivityData[];
  userId: string;
  weekId?: string;
}

/* ================= Icon Mapper ================= */

const iconMap: Record<string, JSX.Element> = {
  "steps.svg": <StepIcon />,
  "activeminutes.svg": <Active24Icon />,
  "nutrition.svg": <NutritionIcon />,
  "sleep.svg": <SleepBedIcon />,
  "water.svg": <WaterGlassIcon />,
  "mindfulness.svg": <MindFullIcon />,
};

const getActivityIcon = (logoName: string) => {
  return iconMap[logoName?.toLowerCase()] || <StepIcon />;
};

/* ================= Helper ================= */

const isNutritionActivity = (activity?: ActivityData | null) => {
  if (!activity) return false;

  return (
    activity.activityLogoName?.toLowerCase() === "nutrition.svg" ||
    activity.activityName?.toLowerCase() === "nutrition"
  );
};

const isVisibleActivity = (activity: ActivityData) => {
  const activityKey = `${activity.activityLogoName} ${activity.activityName}`.toLowerCase();

  return (
    activityKey.includes("activeminutes") ||
    activityKey.includes("active minutes") ||
    isNutritionActivity(activity)
  );
};

const getActivityValueParts = (value: ActivityData["activityValues"]) => {
  if (value === null || value === undefined) return [];

  const values = Array.isArray(value) ? value : [value];

  return values
    .flatMap((item) => String(item).split(","))
    .map((item) => item.trim())
    .filter(Boolean);
};

const isActivityValueEmpty = (value: ActivityData["activityValues"]) => {
  return getActivityValueParts(value).length === 0;
};

// 👇 Extract max number from string like "5000 - 10000"
const getMaxValue = (value: any): number => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const numbers = value.match(/\d+/g)?.map(Number);
    if (!numbers || numbers.length === 0) return 0;
    return Math.max(...numbers);
  }

  return 0;
};

const getActivityValueForApi = (value: string) => {
  const range = value.match(/\d+\s*-\s*\d+/);

  if (range) {
    return range[0].replace(/\s*-\s*/, " - ");
  }

  return value.match(/\d+/)?.[0] || value;
};

const getActivityDisplayValue = (activity: ActivityData) => {
  if (
    activity.activityValues === null ||
    activity.activityValues === undefined ||
    activity.activityValues === ""
  ) {
    return "";
  }

  const value = String(activity.activityValues);

  if (/[a-z]/i.test(value)) {
    return value;
  }

  const activityKey =
    `${activity.activityLogoName} ${activity.activityName}`.toLowerCase();

  if (
    activityKey.includes("activeminutes") ||
    activityKey.includes("active minutes") ||
    activityKey.includes("mindfulness")
  ) {
    return `${value} minutes`;
  }

  if (activityKey.includes("sleep")) {
    return `${value} hours`;
  }

  if (activityKey.includes("water")) {
    return `${value} Glasses`;
  }

  return value;
};

const UserActivity: React.FC<UserActivityProps> = ({
  activityData,
  userId,
  weekId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityData | null>(
    null,
  );
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [activityModalMode, setActivityModalMode] = useState<"edit" | "view">(
    "edit",
  );
  const [loading, setLoading] = useState(false);

  /* ================= Derived Activity UI Data ================= */

  const processedActivities = useMemo(() => {
    return (
      activityData
        ?.slice()
        ?.filter(isVisibleActivity)
        ?.sort(
          (a, b) => (a.activitySortOrder ?? 0) - (b.activitySortOrder ?? 0),
        )
        ?.map((activity) => {
          const isEmpty = isActivityValueEmpty(activity.activityValues);

          let percentage = 0;

          // 👇 Nutrition special case
          if (isNutritionActivity(activity) && !isEmpty) {
            percentage = 100;
          } else {
            const value = getMaxValue(activity.activityValues);
            const goal = Number(activity.goal) || 0;

            if (goal > 0) {
              percentage = Math.min(100, Math.round((value / goal) * 100));
            }
          }

          let status = "";

          if (!isEmpty) {
            status =
              percentage >= 100
                ? "Achieved"
                : percentage >= 70
                  ? "In Progress"
                  : "Needs Attention";
          }

          return {
            ...activity,
            percentage,
            status,
          };
        }) || []
    );
  }, [activityData]);

  const completedCount = processedActivities.filter(
    (a) => a.status === "Achieved",
  ).length;

  const openModal = (activity: ActivityData) => {
    setSelectedActivity(activity);
    setActivityModalMode("edit");

    // 👇 Pre-select existing value in modal
    const activityValue =
      activity.activityValues !== null && activity.activityValues !== undefined
        ? String(activity.activityValues)
        : "";

    const matchedOption = activity.activityOption?.find(
      (option) =>
        option === activityValue ||
        getActivityValueForApi(option) ===
          getActivityValueForApi(activityValue),
    );

    if (isNutritionActivity(activity)) {
      const currentValues = getActivityValueParts(activity.activityValues);
      const selectedNutritionOptions = currentValues.map((currentValue) => {
        return (
          activity.activityOption?.find(
            (option) =>
              option.toLowerCase() === currentValue.toLowerCase() ||
              getActivityValueForApi(option).toLowerCase() ===
                getActivityValueForApi(currentValue).toLowerCase(),
          ) || currentValue
        );
      });

      setSelectedOptions(selectedNutritionOptions);
      setSelectedOption("");
    } else {
      setSelectedOptions([]);
      setSelectedOption(matchedOption || activityValue);
    }

    setModalOpen(true);
  };

  /* ================= API INTEGRATION ================= */

  const handleUpdate = async () => {
    if (!selectedActivity) return;

    const isNutrition = isNutritionActivity(selectedActivity);
    if (isNutrition ? selectedOptions.length === 0 : !selectedOption) return;

    try {
      setLoading(true);
      const activityDate = new Date().toISOString().split("T")[0];
      const isEmpty =
        selectedActivity.activityMappingId === null ||
        selectedActivity.activityMappingId === "";
      const activityValues = isNutrition
        ? [selectedOptions.join(",")]
        : getActivityValueForApi(selectedOption);

      if (isEmpty) {
        await addUserActivity(userId, {
          activityId: selectedActivity.activityId,
          activityValues,
          activityDate,
        });
      } else {
        await updateUserActivity(userId, {
          activityMappingId: selectedActivity.activityMappingId,
          activityValues,
          activityDate,
        });
      }

      const today = new Date().toISOString().split("T")[0];

      dispatch(
        fetchUserDetails({
          userId: userId,
          startDate: today,
          endDate: today,
          ...(weekId && { weekId: weekId }),
        }),
      );

      setModalOpen(false);
      setSelectedActivity(null);
      setSelectedOption("");
      setSelectedOptions([]);
      setActivityModalMode("edit");
    } catch (error) {
      console.error("Activity update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderActivityValue = (activity: ActivityData) => {
    if (!isNutritionActivity(activity)) {
      return getActivityDisplayValue(activity);
    }

    const nutritionValues = getActivityValueParts(activity.activityValues);

    if (nutritionValues.length === 0) {
      return "";
    }

    const hiddenValueCount = nutritionValues.length - 1;

    if (hiddenValueCount === 0) {
      return nutritionValues[0];
    }

    return (
      <button
        type="button"
        className="nutrition-value-summary has-multiple-values"
        onClick={(event) => {
          event.stopPropagation();
          setSelectedActivity(activity);
          setSelectedOption("");
          setSelectedOptions(nutritionValues);
          setActivityModalMode("view");
          setModalOpen(true);
        }}
      >
        <span className="nutrition-value-primary">{nutritionValues[0]}</span>
        <span className="nutrition-more-count">+{hiddenValueCount}</span>
      </button>
    );
  };

  return (
    <div className="activity-container">
      <div className="activity-header">
        <h2>User Activity Progress</h2>
        <div className="activity-controls">
          <span className="completed">
            {completedCount}/{processedActivities.length} Completed
          </span>
        </div>
      </div>

      <div className="activity-grid">
        {processedActivities?.map((activity) => (
          <div
            key={activity.activityId}
            className={`activity-card ${activity.activityName} ${
              isNutritionActivity(activity) ? "is-nutrition" : ""
            }`}
          >
            <div className="card-header">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div className="card-header-icon">
                  {getActivityIcon(activity.activityLogoName)}
                </div>
                <div style={{ marginTop: "4px", textTransform: "capitalize" }}>
                  <h3>{activity.activityName}</h3>
                  <p className="desc">{activity.activityDescription}</p>
                </div>
              </div>

              <PlusCircleIcon
                fontSize={"25px"}
                style={{ cursor: "pointer" }}
                onClick={() => openModal(activity)}
              />
            </div>
            <div className="activity-progress-area">
              <div className="activity-value-row">
                <div className="value">{renderActivityValue(activity)}</div>

                <div
                  className={`status ${activity.status
                    .toLowerCase()
                    .replace(" ", "-")}`}
                >
                  {activity.status}
                </div>
              </div>
              <div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(activity.percentage, 100)}%`,
                    }}
                  ></div>
                </div>

                <div className="card-footer">
                  {Number(activity.goal) !== 0 ? (
                    <span>
                      Goal: {activity.goal} {activity.goalUnit}
                    </span>
                  ) : (
                    <span>
                      Goal:{" "}
                      {activity.goalUnit?.charAt(0).toUpperCase() +
                        activity.goalUnit?.slice(1)}
                    </span>
                  )}
                  <span>{activity.percentage}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ActivityModal
        isOpen={modalOpen}
        title={selectedActivity?.activityName || ""}
        options={
          activityModalMode === "view"
            ? selectedOptions
            : selectedActivity?.activityOption || []
        }
        selectedOption={selectedOption}
        selectedOptions={selectedOptions}
        isMultiSelect={isNutritionActivity(selectedActivity)}
        readOnly={activityModalMode === "view"}
        hideSubmit={activityModalMode === "view"}
        onSelect={setSelectedOption}
        onMultiSelect={setSelectedOptions}
        onClose={() => {
          setModalOpen(false);
          setActivityModalMode("edit");
        }}
        onUpdate={handleUpdate}
        loading={loading}
      />
    </div>
  );
};

export default UserActivity;
